/**
 * The "Before you send" checklist, computed live from the draft so the
 * toolbar's issue count and the send sheet agree. Codes match the backend's
 * `presend_issues`, which re-checks on send and is shown if it disagrees.
 */
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakePresendIssue
} from "@/api/types/outgoingIntake";
import {
  describeShowIf,
  showIfProblem
} from "@/features/foreignNationalOutgoingIntakes/conditional";

import { stepLabel, stepsOf } from "./stepModel";

export type PresendItem = {
  severity: "ok" | "warning" | "error";
  code: string;
  message: string;
  /** The node a Fix button selects. */
  nodeId: string | null;
  fix?: "Fix";
};

const isEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const questionsOf = (schema: OutgoingIntakeFormNode[]) => {
  const out: OutgoingIntakeFormNode[] = [];
  const walk = (nodes: OutgoingIntakeFormNode[]) => {
    for (const n of nodes) {
      if (n.node_type === "question") out.push(n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(schema);
  return out;
};

export const presendChecklist = (
  schema: OutgoingIntakeFormNode[],
  recipientEmail: string
): PresendItem[] => {
  const items: PresendItem[] = [];
  const steps = stepsOf(schema);
  const questions = questionsOf(schema);
  if (!steps.length) {
    items.push({
      severity: "error",
      code: "no_questions",
      message: "Add at least one question",
      nodeId: null
    });
  } else {
    const sections = new Set(steps.map((s) => s.sectionId).filter(Boolean))
      .size;
    const uploads = questions.filter((q) => q.question_type === "file").length;
    items.push({
      severity: "ok",
      code: "summary",
      message: [
        `${plural(steps.length, "question")}${
          sections > 1 ? ` in ${sections} sections` : ""
        }`,
        uploads ? plural(uploads, "upload") : null
      ]
        .filter(Boolean)
        .join(", "),
      nodeId: null
    });
  }

  questions.forEach((q) => {
    const label = stepLabel(q);
    if (!String(q.prompt ?? q.title ?? "").trim())
      items.push({
        severity: "error",
        code: "empty_prompt",
        message: "A question has no prompt",
        nodeId: q.node_id,
        fix: "Fix"
      });
    if (
      (q.question_type === "single_select" ||
        q.question_type === "multi_select") &&
      !(q.options ?? []).some((o) => String(o.label ?? "").trim())
    )
      items.push({
        severity: "error",
        code: "no_options",
        message: `“${label}” has no choices`,
        nodeId: q.node_id,
        fix: "Fix"
      });
  });

  steps.forEach(({ node }) => {
    if (!node.show_if?.question_id) return;
    const problem = showIfProblem(node, schema);
    items.push(
      problem
        ? {
            severity: "error",
            code: "broken_condition",
            message: `“${stepLabel(node)}” ${problem}`,
            nodeId: node.node_id,
            fix: "Fix"
          }
        : {
            severity: "ok",
            code: "condition",
            message: `“${stepLabel(node)}” shows ${describeShowIf(
              node,
              schema
            )}`,
            nodeId: node.node_id
          }
    );
  });

  const email = recipientEmail.trim();
  items.push(
    !email
      ? {
          severity: "error",
          code: "missing_recipient",
          message: "Add the recipient's email",
          nodeId: null
        }
      : !isEmail(email)
      ? {
          severity: "error",
          code: "invalid_recipient",
          message: `“${email}” isn't a valid email`,
          nodeId: null
        }
      : {
          severity: "ok",
          code: "recipient",
          message: "Recipient email looks valid",
          nodeId: null
        }
  );
  return items;
};

/** Merge the server's issues (from a 400 on send) into the local list. */
export const withServerIssues = (
  items: PresendItem[],
  server: OutgoingIntakePresendIssue[]
): PresendItem[] => {
  const seen = new Set(items.map((i) => `${i.code}|${i.nodeId ?? ""}`));
  const extra = server
    .filter((s) => !seen.has(`${s.code}|${s.node_id ?? ""}`))
    .map<PresendItem>((s) => ({
      severity: s.severity === "warning" ? "warning" : "error",
      code: s.code,
      message: s.message,
      nodeId: s.node_id ?? null,
      fix: s.node_id ? "Fix" : undefined
    }));
  return [...items, ...extra];
};

export const errorCount = (items: PresendItem[]) =>
  items.filter((i) => i.severity === "error").length;
